import { createSelector } from '@ngrx/store';
import { IRootState } from '../../../store/index';
import {
  ActionTypes,
  TypeAction
} from './tax-document.actions';
import {
  createSelectModelFromStringArrayWithBadges
} from '../../../shared/components/select/models/createSelectModel';
import { IRecruitTaxInfo } from '../../../core/services/tax-documents/interfaces';

export interface ITaxFormData {
  fullName: string;
  businessName: string;
  businessType: string;
  exemptPayeeCode: string | null;
  exemptionFromFATCA: string | null;
  addressLine: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  tin: string;
  ssn: string | null;
  ein: string | null;
  signature: string;
}

export interface ITaxSelectModels {
  businessType: string[];
}

export interface IState {
  selectModels: ITaxSelectModels;
  formData: ITaxFormData;
  taxDocumentData: IRecruitTaxInfo;
  isSsnLoading: boolean;
  hasFetchedSsn: boolean;
}

const initialState: IState = {
  selectModels: {
    businessType: []
  },
  formData: {
    fullName: '',
    businessName: '',
    businessType: '',
    exemptPayeeCode: null,
    exemptionFromFATCA: null,
    addressLine: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
    tin: '',
    ssn: null,
    ein: null,
    signature: '',
  },
  taxDocumentData: {
    employeeId: null,
    businessName: '',
    businessType: '',
    tin: '',
    fullName: '',
    zip: '',
    addressLine: '',
    addressLine2: '',
    city: '',
    state: '',
  },
  isSsnLoading: false,
  hasFetchedSsn: false,
};

function formatSsn(raw: string | null): string | null {
  if (!raw) return raw;
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== 9) return raw;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

export function taxDocumentReducer(state: IState = initialState, action: TypeAction): IState {
  const { type, payload } = action;

  switch (type) {
    case ActionTypes.FORM_CONTROL_CHANGE: {
      const { formData } = state;
      const { fieldId, value } = payload;
      const result = Object.assign({}, formData, { [fieldId]: value });

      return Object.assign({}, state, { formData: result });
    }

    case ActionTypes.STORE_TAX_TYPES: {
      return Object.assign({}, state, { selectModels: payload });
    }

    case ActionTypes.STORE_RECRUIT_FORM_DATA: {
      const { formData } = state;
      const result = {
        ...formData,
        ...payload
      };

      return Object.assign({}, state, { formData: result });
    }

    case ActionTypes.STORE_TAX_INFO: {
      return Object.assign({}, state, { taxDocumentData: payload });
    }

    case ActionTypes.FETCH_SSN: {
      return {
        ...state,
        isSsnLoading: true,
      };
    }

    case ActionTypes.FETCH_SSN_SUCCESS: {
      const { formData } = state;
      const incoming = formatSsn(action.payload.ssn);
      const result = {
        ...formData,
        
        ssn: incoming === null ? formData.ssn : incoming,
      };

      return {
        ...state,
        formData: result,
        isSsnLoading: false,
        hasFetchedSsn: true,
      };
    }

    default: {
      return state;
    }
  }
}

export const getTaxFormData = createSelector(
  (root: IRootState): IState => root.taxDocuments,
  ({ formData }: IState): ITaxFormData => formData
);

export const getTaxSelectModels = createSelector(
  (root: IRootState): IState => root.taxDocuments,
  ({ selectModels }: IState): any => {
    return Object.keys(selectModels).reduce((acc, key) => {
      const badgesInfo = [{ optionKey: "Individual/Sole Proprietor", badgeText: "Most Common" }];
      acc[key] = createSelectModelFromStringArrayWithBadges(selectModels[key], badgesInfo);

      return acc;
    }, {});
  }
);

export const getTaxInfo = createSelector(
  (root: IRootState): IState => root.taxDocuments,
  ({ taxDocumentData }: IState): IRecruitTaxInfo => taxDocumentData
);

export const getIsSsnLoading = createSelector(
  (root: IRootState): IState => root.taxDocuments,
  (state: IState): boolean => state.isSsnLoading
);

export const getHasFetchedSsn = createSelector(
  (root: IRootState): IState => root.taxDocuments,
  (state: IState): boolean => state.hasFetchedSsn
);
